import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box, Typography, Button, Table, TableHead, TableRow, TableCell,
  TableBody, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Snackbar, Alert, Paper
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import  api, { API_BASE_URL } from "../../utils/api";

const WorkOrderManager = () => {
  const [types, setTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingSubtype, setEditingSubtype] = useState(null);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({ type: "", id: null });
  const [subtypeDialog, setSubtypeDialog] = useState(false);
  const [locationDialog, setLocationDialog] = useState(false);
  const [subtypeForm, setSubtypeForm] = useState({ id: null, type_id: null, subtype_name: "" });
  const [locationForm, setLocationForm] = useState({ id: null, type_id: null, location_name: "", description: "" });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    fetchTypes();
    fetchSubtypes();
    fetchLocations();
  }, []);

  const fetchTypes = async () => {
    const res = await api.get(`/workorder-types`);
    setTypes(res.data);
  };

  const fetchSubtypes = async () => {
    const res = await api.get(`/workorder-subtypes`);
    setSubtypes(res.data);
  };

  const fetchLocations = async () => {
    const res = await api.get(`/workorder-locations`);
    setLocations(res.data);
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => setSnackbar({ ...snackbar, open: false });

  // Type logic
  const handleInputChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const openAddDialog = (type = null) => {
    if (type) {
      setFormData(type);
      setIsEditing(true);
    } else {
      setFormData({ type: "", id: null });
      setIsEditing(false);
    }
    setOpenDialog(true);
  };

  const closeDialog = () => {
    setOpenDialog(false);
    setFormData({ type: "", id: null });
    setIsEditing(false);
  };

  const handleFormSubmit = async () => {
    try {
      if (isEditing) {
        await api.put(`/workorder-types/${formData.id}`, formData);
        showSnackbar("Work Order Type updated!");
      } else {
        await api.post(`/workorder-types`, formData);
        showSnackbar("Work Order Type added!");
      }
      fetchTypes();
      closeDialog();
    } catch (err) {
      showSnackbar("Error saving type", "error");
    }
  };

  const confirmDeleteType = async (id) => {
    const relatedSubtypes = subtypes.filter(s => s.type_id === id);
    const relatedLocations = locations.filter(l => l.type_id === id);
    const typeLabel = types.find(t => t.id === id)?.type;

    const warning = `Deleting "${typeLabel}" will also remove ${relatedSubtypes.length} subtypes and ${relatedLocations.length} locations. Continue?`;

    if (window.confirm(warning)) {
      try {
        await api.delete(`/workorder-types/${id}`);
        showSnackbar("Type, subtypes, and locations deleted!");
        fetchTypes();
        fetchSubtypes();
        fetchLocations();
      } catch (err) {
        showSnackbar("Error deleting type", "error");
      }
    }
  };

  // Subtype logic
  const openSubtypeDialog = (type_id) => {
    setSubtypeForm({ id: null, type_id, subtype_name: "" });
    setEditingSubtype(null);
    setSubtypeDialog(true);
  };

  const editSubtype = (subtype) => {
    setSubtypeForm(subtype);
    setEditingSubtype(subtype.id);
    setSubtypeDialog(true);
  };

  const handleSubtypeChange = (e) => {
    setSubtypeForm({ ...subtypeForm, [e.target.name]: e.target.value });
  };

  const handleSubtypeSubmit = async () => {
    try {
      if (editingSubtype) {
        await api.put(`/workorder-subtypes/${editingSubtype}`, subtypeForm);
        showSnackbar("Subtype updated!");
      } else {
        await api.post(`/workorder-subtypes`, subtypeForm);
        showSnackbar("Subtype added!");
      }
      fetchSubtypes();
      setSubtypeDialog(false);
    } catch (err) {
      showSnackbar("Error saving subtype", "error");
    }
  };

  const deleteSubtype = async (id) => {
    if (!window.confirm("Delete this subtype?")) return;
    try {
      await api.delete(`/workorder-subtypes/${id}`);
      showSnackbar("Subtype deleted!");
      fetchSubtypes();
    } catch (err) {
      showSnackbar("Error deleting subtype", "error");
    }
  };

  // Location logic
  const openLocationDialog = (type_id) => {
    setLocationForm({ id: null, type_id, location_name: "", description: "" });
    setEditingLocation(null);
    setLocationDialog(true);
  };

  const editLocation = (location) => {
    setLocationForm(location);
    setEditingLocation(location.id);
    setLocationDialog(true);
  };

  const handleLocationChange = (e) => {
    setLocationForm({ ...locationForm, [e.target.name]: e.target.value });
  };

  const handleLocationSubmit = async () => {
    try {
      if (editingLocation) {
        await api.put(`/workorder-locations/${editingLocation}`, locationForm);
        showSnackbar("Location updated!");
      } else {
        await api.post(`/workorder-locations`, locationForm);
        showSnackbar("Location added!");
      }
      fetchLocations();
      setLocationDialog(false);
    } catch (err) {
      showSnackbar("Error saving location", "error");
    }
  };

  const deleteLocation = async (id) => {
    if (!window.confirm("Delete this location?")) return;
    try {
      await api.delete(`/workorder-locations/${id}`);
      showSnackbar("Location deleted!");
      fetchLocations();
    } catch (err) {
      showSnackbar("Error deleting location", "error");
    }
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", mt: 4 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        🛠 Work Order Types, Subtypes & Locations
      </Typography>

      <Button variant="contained" onClick={() => openAddDialog()} sx={{ mb: 2 }}>
        ➕ Add Work Order Type
      </Button>

      <Paper elevation={3}>
        <Table>
          <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
            <TableRow>
             
              <TableCell>Type</TableCell>
              <TableCell>Subtypes</TableCell>
              <TableCell>Locations</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {types.map((type) => (
              <TableRow key={type.id}>
                
                <TableCell>{type.type}</TableCell>
                <TableCell>
                  {subtypes.filter(s => s.type_id === type.id).map(s => (
                    <Box key={s.id} sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2">— {s.subtype_name}</Typography>
                      <Box>
                        <IconButton size="small" onClick={() => editSubtype(s)}><Edit fontSize="inherit" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => deleteSubtype(s.id)}><Delete fontSize="inherit" /></IconButton>
                      </Box>
                    </Box>
                  ))}
                  <Button size="small" onClick={() => openSubtypeDialog(type.id)} sx={{ mt: 1 }}>
                    ➕ Add Subtype
                  </Button>
                </TableCell>
                <TableCell>
                  {locations.filter(l => l.type_id === type.id).map(loc => (
                    <Box key={loc.id} sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2">📍 {loc.location_name}</Typography>
                      <Box>
                        <IconButton size="small" onClick={() => editLocation(loc)}><Edit fontSize="inherit" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => deleteLocation(loc.id)}><Delete fontSize="inherit" /></IconButton>
                      </Box>
                    </Box>
                  ))}
                  <Button size="small" onClick={() => openLocationDialog(type.id)} sx={{ mt: 1 }}>
                    ➕ Add Location
                  </Button>
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => openAddDialog(type)}><Edit /></IconButton>
                  <IconButton color="error" onClick={() => confirmDeleteType(type.id)}><Delete /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Type Dialog */}
      <Dialog open={openDialog} onClose={closeDialog} fullWidth>
        <DialogTitle>{isEditing ? "Edit" : "Add"} Work Order Type</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Type" name="type" value={formData.type} onChange={handleInputChange} />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={handleFormSubmit} variant="contained">{isEditing ? "Update" : "Add"}</Button>
        </DialogActions>
      </Dialog>

      {/* Subtype Dialog */}
      <Dialog open={subtypeDialog} onClose={() => setSubtypeDialog(false)} fullWidth>
        <DialogTitle>{editingSubtype ? "Edit" : "Add"} Subtype</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Subtype Name"
            name="subtype_name"
            value={subtypeForm.subtype_name}
            onChange={handleSubtypeChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubtypeDialog(false)}>Cancel</Button>
          <Button onClick={handleSubtypeSubmit} variant="contained">{editingSubtype ? "Update" : "Add"}</Button>
        </DialogActions>
      </Dialog>

      {/* Location Dialog */}
      <Dialog open={locationDialog} onClose={() => setLocationDialog(false)} fullWidth>
        <DialogTitle>{editingLocation ? "Edit" : "Add"} Location</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Location Name"
            name="location_name"
            value={locationForm.location_name}
            onChange={handleLocationChange}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={locationForm.description}
            onChange={handleLocationChange}
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLocationDialog(false)}>Cancel</Button>
          <Button onClick={handleLocationSubmit} variant="contained">{editingLocation ? "Update" : "Add"}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={closeSnackbar}>
        <Alert severity={snackbar.severity} onClose={closeSnackbar}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default WorkOrderManager;
